import { Injectable } from '@angular/core';
import {
  catchError,
  filter,
  from,
  map,
  mergeMap,
  Observable,
  of,
  switchMap,
  take,
  tap,
  throwError,
  timeout,
  toArray
} from 'rxjs';
import { BaseDomainModel } from '../../../models/base/base-domain-model';
import { Asset } from '../../../models/image/dto/asset';
import { BulkEditAssetsRequest } from '../../../models/image/requests/bulk-edit-assets-request';
import { BulkEditAssetsResponse } from '../../../models/image/responses/bulk-edit-assets-response';
import { MediaManagerFolderDto } from '../../../models/image/dto/media-manager-folder-dto';
import { exists } from '../../../functions/exists';
import {
  MediaManagerAssetDeleteState,
  MediaManagerAssetDeleteStatus,
  MediaManagerLibraryContext,
  MediaManagerStore
} from '../store';
import { applyBulkEditResponse, applyDeletedAssets, findAssetsInLibrary, findFoldersInLibrary } from '../mutations';
import {
  MediaManagerPreparedAssetDelete,
  preflightBulkEditValidation,
  preflightDeleteAssetsValidation,
  prepareAssetDeletes
} from '../validation';
import { AssetRepository } from '../../../repositories/asset/asset-repository';

/**
 * REST-based asset mutations: metadata update, single/multi delete, and bulk edit (move + metadata).
 *
 * Delete flows are batched by a client-generated batch id so a user can queue several deletes and confirm
 * them together. Each item's per-batch state (pending -> confirmed -> in-progress -> completed/failed)
 * lives in the store's assetDeleteStates$ so the folder sync service knows to skip WS reconciliation
 * for locally-in-progress deletes.
 */
@Injectable() // Provided by Logged In Scope
export class MediaManagerAssetService extends BaseDomainModel {
  constructor(
    private assetRepository: AssetRepository,
    private store: MediaManagerStore
  ) {
    super();
  }

  /**
   * Trim and de-duplicate a batch of asset unique ids. Exposed publicly so sibling services (e.g. bulk
   * delete) can normalize inputs the same way before comparing against delete state.
   */
  public normalizeAssetDeleteIds(assetUniqueIds: string[]): string[] {
    return Array.from(
      new Set(
        (assetUniqueIds || [])
          .map(assetUniqueId => assetUniqueId?.trim())
          .filter(assetUniqueId => exists(assetUniqueId))
      )
    );
  }

  private createAssetDeleteBatchId(): string {
    return `asset-delete-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  deleteAsset$(assetUniqueId: string): Observable<string> {
    return this.deleteAssets$([assetUniqueId]).pipe(map(deletedAssetIds => deletedAssetIds[0] ?? assetUniqueId));
  }

  /**
   * Two-phase batched asset delete. queueDeleteAssets$ puts every id into 'delete-pending' state under one
   * batch id (rejecting anything already in-flight). confirmDeleteBatch$ runs the preflight, transitions
   * to 'delete-in-progress', then calls the REST delete per item and patches state.
   */
  deleteAssets$(assetUniqueIds: string[]): Observable<string[]> {
    return this.queueDeleteAssets$(assetUniqueIds).pipe(switchMap(batchId => this.confirmDeleteBatch$(batchId)));
  }

  private queueDeleteAssets$(assetUniqueIds: string[]): Observable<string> {
    const normalizedAssetUniqueIds = this.normalizeAssetDeleteIds(assetUniqueIds);
    if (!normalizedAssetUniqueIds.length) {
      return throwError(() => new Error('No assets were selected for deletion.'));
    }
    // Use the BehaviorSubject snapshot directly so the duplicate-check and pending-state write stay in one
    // synchronous call stack. A take(1) pipeline makes that atomicity less obvious to future readers.
    const deleteStates = this.store.getAssetDeleteStatesValue();
    const blockedAssetUniqueIds = normalizedAssetUniqueIds.filter(assetUniqueId => {
      const currentState = deleteStates.get(assetUniqueId);
      return (
        currentState?.status === 'delete-pending' ||
        currentState?.status === 'delete-confirmed' ||
        currentState?.status === 'delete-in-progress'
      );
    });
    if (blockedAssetUniqueIds.length) {
      return throwError(
        () => new Error(`Asset delete is already in progress for '${blockedAssetUniqueIds.join("', '")}'`)
      );
    }
    const batchId = this.createAssetDeleteBatchId();
    const nextDeleteStates = new Map(deleteStates);
    normalizedAssetUniqueIds.forEach(assetUniqueId => {
      nextDeleteStates.set(assetUniqueId, {
        assetUniqueId,
        batchId,
        status: 'delete-pending',
        errorMessage: null
      });
    });
    this.store.connectToAssetDeleteStates(nextDeleteStates);
    return of(batchId);
  }

  confirmDeleteBatch$(batchId: string): Observable<string[]> {
    return this.getPendingAssetDeleteBatchIds$(batchId).pipe(
      switchMap(assetUniqueIds => {
        if (!assetUniqueIds.length) {
          return throwError(() => new Error(`Asset delete batch '${batchId}' is not pending.`));
        }
        return this.patchAssetDeleteStatus$(assetUniqueIds, batchId, 'delete-confirmed', null).pipe(
          switchMap(() => {
            return this.store.getActiveLibraryContext$().pipe(
              switchMap(context => {
                const { filesystem, assetLibrary } = context;
                const preparedDeletes = prepareAssetDeletes(assetUniqueIds, assetLibrary);
                const preflightErrors = preflightDeleteAssetsValidation(
                  assetUniqueIds,
                  preparedDeletes,
                  filesystem,
                  assetLibrary
                );
                if (preflightErrors) {
                  return this.patchAssetDeleteStatus$(assetUniqueIds, batchId, 'delete-failed', preflightErrors).pipe(
                    switchMap(() => throwError(() => new Error(preflightErrors)))
                  );
                }
                return this.patchAssetDeleteStatus$(assetUniqueIds, batchId, 'delete-in-progress', null).pipe(
                  switchMap(() => {
                    return this.deleteAssetsWithValidatedState$(preparedDeletes, batchId, context);
                  })
                );
              })
            );
          })
        );
      }),
      catchError(err => {
        return this.failAssetDeleteBatch$(batchId, err).pipe(
          switchMap(() => this.clearAssetDeleteBatch$(batchId)),
          switchMap(() => throwError(() => err))
        );
      })
    );
  }

  private deleteAssetsWithValidatedState$(
    preparedDeletes: MediaManagerPreparedAssetDelete[],
    batchId: string,
    context: MediaManagerLibraryContext
  ): Observable<string[]> {
    const { brandId, filesystem, assetLibrary } = context;
    return from(preparedDeletes).pipe(
      mergeMap(preparedDelete => {
        return this.assetRepository.deleteAsset$(preparedDelete.asset.id, preparedDelete.asset.md5Hash).pipe(
          switchMap(() => {
            return this.patchAssetDeleteStatus$([preparedDelete.assetUniqueId], batchId, 'delete-completed', null).pipe(
              map(() => preparedDelete.assetUniqueId)
            );
          })
        );
      }, 4),
      toArray(),
      switchMap(deletedAssetUniqueIds => {
        const inMemoryPatchSucceeded = applyDeletedAssets(deletedAssetUniqueIds, filesystem, assetLibrary);
        if (!inMemoryPatchSucceeded) {
          return throwError(
            () => new Error('Incremental patch failed to delete assets. AssetLibrary fetched and rebuilt Filesystem.')
          );
        }
        this.store.notifyFilesystemChanged('assets deleted');
        this.startWaitingForDeletedAssetToasts(deletedAssetUniqueIds);
        return this.clearAssetDeleteBatch$(batchId).pipe(map(() => deletedAssetUniqueIds));
      }),
      catchError(err => {
        return this.store.refreshBrandAssetLibrary$(brandId).pipe(
          switchMap(() => {
            return throwError(() => err);
          })
        );
      })
    );
  }

  private getPendingAssetDeleteBatchIds$(batchId: string): Observable<string[]> {
    if (!batchId?.trim()) return of([]);
    return this.store.assetDeleteStates$.pipe(
      take(1),
      map(deleteStates => {
        return Array.from(deleteStates.values())
          .filter(deleteState => deleteState.batchId === batchId && deleteState.status === 'delete-pending')
          .map(deleteState => deleteState.assetUniqueId);
      })
    );
  }

  /**
   * Public because MediaManagerBulkDeleteService needs to patch delete state as items complete via the
   * WebSocket path (same lifecycle transitions, different transport).
   */
  public patchAssetDeleteStatus$(
    assetUniqueIds: string[],
    batchId: string,
    status: MediaManagerAssetDeleteStatus,
    errorMessage: string | null
  ): Observable<void> {
    return this.patchAssetDeleteStates$(
      this.normalizeAssetDeleteIds(assetUniqueIds).map(assetUniqueId => ({
        assetUniqueId,
        batchId,
        status,
        errorMessage
      }))
    );
  }

  private patchAssetDeleteStates$(deleteStates: MediaManagerAssetDeleteState[]): Observable<void> {
    if (!deleteStates.length) return of(undefined);
    return this.updateAssetDeleteStates$(currentState => {
      const nextState = new Map(currentState);
      for (const deleteState of deleteStates) {
        nextState.set(deleteState.assetUniqueId, deleteState);
      }
      return nextState;
    });
  }

  private updateAssetDeleteStates$(
    updater: (currentState: Map<string, MediaManagerAssetDeleteState>) => Map<string, MediaManagerAssetDeleteState>
  ): Observable<void> {
    return this.store.assetDeleteStates$.pipe(
      take(1),
      tap(currentState => this.store.connectToAssetDeleteStates(updater(currentState))),
      map((): void => undefined)
    );
  }

  /** Public: MediaManagerBulkDeleteService uses this to clear its own batch when the WS job returns. */
  public clearAssetDeleteBatch$(batchId: string): Observable<void> {
    if (!batchId?.trim()) return of(undefined);
    return this.updateAssetDeleteStates$(currentState => {
      const nextState = new Map(currentState);
      Array.from(nextState.entries())
        .filter(([, deleteState]) => deleteState.batchId === batchId)
        .forEach(([assetUniqueId]) => nextState.delete(assetUniqueId));
      return nextState;
    });
  }

  private failAssetDeleteBatch$(batchId: string, err: any): Observable<void> {
    return this.store.assetDeleteStates$.pipe(
      take(1),
      switchMap(deleteStates => {
        const assetUniqueIds = Array.from(deleteStates.values())
          .filter(deleteState => deleteState.batchId === batchId)
          .filter(deleteState => deleteState.status !== 'delete-completed')
          .map(deleteState => deleteState.assetUniqueId);
        if (!assetUniqueIds.length) return of(undefined);
        const errorMessage = err?.message || err?.error?.message || 'Asset deletion failed.';
        return this.patchAssetDeleteStatus$(assetUniqueIds, batchId, 'delete-failed', errorMessage);
      })
    );
  }

  /**
   * After the REST deletes complete, listen for the matching WS asset-deleted notifications so the toast
   * says '<n> media items deleted!' at the right moment (after the backend has fully processed each
   * delete, not just acknowledged the REST call).
   */
  private startWaitingForDeletedAssetToasts(assetUniqueIds: string[]): void {
    this.waitForDeletedAssets$(assetUniqueIds).subscribeWhileAlive({
      owner: this,
      error: err => console.info(err?.message || err)
    });
  }

  private waitForDeletedAssets$(assetUniqueIds: string[]): Observable<string[]> {
    const pendingAssetIds = new Set(this.normalizeAssetDeleteIds(assetUniqueIds));
    if (!pendingAssetIds.size) return of([]);
    return this.store.assetDeletedMessages$.pipe(
      map(message => message?.getAssetUniqueId()),
      filter(id => exists(id) && pendingAssetIds.has(id)),
      take(pendingAssetIds.size),
      toArray(),
      tap(deletedAssetIds => this.publishAssetDeleteSuccessToast(deletedAssetIds.length)),
      timeout({ first: 10000, each: 10000 }),
      catchError(() => of([]))
    );
  }

  private publishAssetDeleteSuccessToast(count: number): void {
    if (count <= 0) return;
    this.store.publishMediaManagerSuccessMessage(`${count} media item${count === 1 ? '' : 's'} deleted!`);
  }

  bulkEditAssets$(req: BulkEditAssetsRequest): Observable<BulkEditAssetsResponse> {
    const normalizedReq =
      window?.injector?.Deserialize?.instanceOf(BulkEditAssetsRequest, req) ?? new BulkEditAssetsRequest();
    return this.store.getActiveLibraryContext$().pipe(
      switchMap(context => {
        const { brandId, filesystem, assetLibrary } = context;
        const canonicalSelectedFolders = findFoldersInLibrary(normalizedReq.updatedAssetFolders, assetLibrary);
        if (!canonicalSelectedFolders) {
          return throwError(() => new Error(`Some MediaManager folders could not be found in assetLibrary`));
        }
        const canonicalSelectedAssets = findAssetsInLibrary(normalizedReq.updatedAssets, assetLibrary);
        if (!canonicalSelectedAssets) {
          return throwError(() => new Error(`Some MediaManager assets could not be found in assetLibrary`));
        }
        const preflightBulkEditErrors = preflightBulkEditValidation(normalizedReq, assetLibrary, filesystem);
        if (preflightBulkEditErrors) {
          return throwError(() => new Error(preflightBulkEditErrors));
        }
        const apiReq = this.buildBulkEditApiRequest(
          new BulkEditAssetsRequest(
            normalizedReq.targetFolderId,
            normalizedReq.updatedAssetFolders,
            this.buildBulkEditApiAssets(normalizedReq.updatedAssets, canonicalSelectedAssets)
          )
        );
        return this.bulkEditAssetsWithValidatedState$(
          apiReq,
          canonicalSelectedFolders,
          canonicalSelectedAssets,
          context
        );
      })
    );
  }

  private bulkEditAssetsWithValidatedState$(
    apiReq: BulkEditAssetsRequest,
    canonicalSelectedFolders: MediaManagerFolderDto[],
    canonicalSelectedAssets: Asset[],
    context: MediaManagerLibraryContext
  ): Observable<BulkEditAssetsResponse> {
    const { brandId, filesystem, assetLibrary } = context;
    return this.assetRepository.bulkEditAssets$(apiReq, context.brandId).pipe(
      switchMap((response: BulkEditAssetsResponse) => {
        const inMemoryPatchSucceeded = applyBulkEditResponse(
          response,
          canonicalSelectedFolders,
          canonicalSelectedAssets,
          filesystem,
          assetLibrary
        );
        if (!inMemoryPatchSucceeded) {
          return this.store.refreshBrandAssetLibrary$(brandId).pipe(
            switchMap(() => {
              return throwError(() => {
                return new Error(
                  'Incremental patch failed to bulk update folders' +
                    ' and/or assets. AssetLibrary fetched and rebuilt Filesystem.'
                );
              });
            })
          );
        }
        this.store.notifyFilesystemChanged('bulk edit assets');
        return of(response);
      })
    );
  }

  private buildBulkEditApiRequest(req: BulkEditAssetsRequest): BulkEditAssetsRequest {
    if (req.targetFolderId !== 'ROOT') {
      return req;
    }
    // The MMFS uses 'ROOT' as its in-memory sentinel folder id, but the bulk edit API expects an empty
    // target folder id when moving items to the actual filesystem root.
    const normalizedReq = window?.injector?.Deserialize?.instanceOf(BulkEditAssetsRequest, req);
    normalizedReq.targetFolderId = '';
    return normalizedReq;
  }

  private buildBulkEditApiAssets(updatedAssets: Asset[], canonicalAssets: Asset[]): Asset[] {
    const canonicalAssetsById = new Map(canonicalAssets.map(asset => [asset.getUniqueIdentifier(), asset]));
    return updatedAssets.map(updatedAsset => {
      const canonicalAsset = canonicalAssetsById.get(updatedAsset.getUniqueIdentifier());
      if (!canonicalAsset) {
        return updatedAsset;
      }
      return this.mergeBulkEditAssetUpdate(canonicalAsset, updatedAsset);
    });
  }

  private mergeBulkEditAssetUpdate(canonicalAsset: Asset, updatedAsset: Asset): Asset {
    // start from the canonical library asset so server-owned fields like timestamp are preserved.
    // callers may pass stale upload snapshots that differ from the persisted asset library state.
    const normalizedAsset = window?.injector?.Deserialize?.instanceOf(Asset, canonicalAsset);
    normalizedAsset.displayName = updatedAsset.displayName;
    normalizedAsset.fileName = updatedAsset.fileName;
    normalizedAsset.parentFolderId = updatedAsset.parentFolderId;
    normalizedAsset.tags = [...(updatedAsset.tags ?? [])];
    normalizedAsset.assetLibraryType = updatedAsset.assetLibraryType;
    normalizedAsset.mediaClass = updatedAsset.mediaClass;
    normalizedAsset.mediaType = updatedAsset.mediaType;
    return normalizedAsset;
  }
}
